import React, { useState, useEffect } from 'react';
import './Admin.css';

const TrainingManager = () => {
    const [items, setItems] = useState([]);
    const [settings, setSettings] = useState({
        title: 'Обучение',
        showTitle: true,
        columns: 3,
        gap: 30
    });
    const [loading, setLoading] = useState(true);
    const [draggedItem, setDraggedItem] = useState(null);
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [itemsRes, settingsRes] = await Promise.all([
                fetch('/api/content/training?field=items'),
                fetch('/api/content/training?field=settings')
            ]);

            const itemsData = await itemsRes.json();
            const settingsData = await settingsRes.json();

            let allItems = itemsData.data || [];
            allItems.sort((a, b) => (a.order || 0) - (b.order || 0));

            setItems(allItems);
            setSettings(settingsData.data || { title: 'Обучение', showTitle: true, columns: 3, gap: 30 });
            setHasChanges(false);
        } catch (error) {
            console.error('Ошибка загрузки:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveItems = async (itemsToSave) => {
        try {
            await fetch('/api/content/training', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ field: 'items', data: itemsToSave })
            });
            setHasChanges(false);
            return true;
        } catch (error) {
            return false;
        }
    };

    const saveSettings = async (newSettings) => {
        try {
            await fetch('/api/content/training', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ field: 'settings', data: newSettings })
            });
            return true;
        } catch (error) {
            return false;
        }
    };

    const showMessage = (text, isError = false) => {
        setMessage(text);
        setTimeout(() => setMessage(''), 2000);
    };

    const addItem = () => {
        const newItem = {
            id: Date.now().toString(),
            name: 'Новый курс',
            price: 15000,
            priceMin: null,
            priceMax: null,
            isRange: false,
            duration: '3 дня',
            description: 'Описание курса',
            imageUrl: null,
            visible: true,
            order: items.length
        };
        setItems([...items, newItem]);
        setHasChanges(true);
        showMessage('✅ Курс добавлен, нажмите "Сохранить"');
    };

    const updateItemField = (id, field, value) => {
        setItems(items.map(i => i.id === id ? { ...i, [field]: value } : i));
        setHasChanges(true);
    };

    const updatePriceType = (id, isRange) => {
        const item = items.find(i => i.id === id);
        if (isRange) {
            updateItemField(id, 'isRange', true);
            updateItemField(id, 'priceMin', item.price || 10000);
            updateItemField(id, 'priceMax', (item.price || 10000) + 5000);
            updateItemField(id, 'price', null);
        } else {
            updateItemField(id, 'isRange', false);
            updateItemField(id, 'price', item.priceMin || 15000);
            updateItemField(id, 'priceMin', null);
            updateItemField(id, 'priceMax', null);
        }
    };

    const deleteItem = (id) => {
        if (!confirm('Удалить курс?')) return;
        const filtered = items.filter(i => i.id !== id);
        const reordered = filtered.map((i, idx) => ({ ...i, order: idx }));
        setItems(reordered);
        setHasChanges(true);
        showMessage('✅ Курс удален, нажмите "Сохранить"');
    };

    const toggleVisibility = (id) => {
        setItems(items.map(i => i.id === id ? { ...i, visible: !i.visible } : i));
        setHasChanges(true);
    };

    const handleSaveAll = async () => {
        const itemsSaved = await saveItems(items);
        const settingsSaved = await saveSettings(settings);
        if (itemsSaved && settingsSaved) {
            showMessage('✅ Все изменения сохранены!');
            setHasChanges(false);
        } else {
            showMessage('❌ Ошибка при сохранении', true);
        }
    };

    const handleImageUpload = async (itemId, file) => {
        if (!file) return;
        setUploading(prev => ({ ...prev, [itemId]: true }));
        const formData = new FormData();
        formData.append('trainingImage', file);

        try {
            const response = await fetch('/api/upload/training-image', {
                method: 'POST',
                body: formData
            });
            if (response.ok) {
                const data = await response.json();
                updateItemField(itemId, 'imageUrl', data.url);
                showMessage('✅ Изображение загружено');
            }
        } catch (error) {
            showMessage('❌ Ошибка загрузки', true);
        } finally {
            setUploading(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => e.preventDefault();

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedItem === null) return;
        const newItems = [...items];
        const [dragged] = newItems.splice(draggedItem, 1);
        newItems.splice(dropIndex, 0, dragged);
        setItems(newItems.map((i, idx) => ({ ...i, order: idx })));
        setHasChanges(true);
        setDraggedItem(null);
    };

    if (loading) return <div className="loading-spinner">Загрузка...</div>;

    return (
        <div className="services-manager">
            <h2>📚 Управление обучением</h2>

            {message && <div className={`message ${message.includes('❌') ? 'error' : 'success'}`}>{message}</div>}

            {hasChanges && (
                <div className="save-banner">
                    <span>⚠️ Есть несохраненные изменения</span>
                    <button onClick={handleSaveAll} className="save-all-btn">💾 Сохранить все изменения</button>
                </div>
            )}

            <div className="settings-section">
                <h3>⚙️ Настройки блока</h3>
                <div className="settings-grid">
                    <div className="setting-item">
                        <label>Заголовок</label>
                        <input type="text" value={settings.title} onChange={(e) => setSettings({...settings, title: e.target.value})} />
                    </div>
                    <div className="setting-item checkbox">
                        <label><input type="checkbox" checked={settings.showTitle} onChange={(e) => setSettings({...settings, showTitle: e.target.checked})} /> Показывать заголовок</label>
                    </div>
                    <div className="setting-item">
                        <label>Колонки</label>
                        <select value={settings.columns} onChange={(e) => setSettings({...settings, columns: Number(e.target.value)})}>
                            <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
                        </select>
                    </div>
                    <div className="setting-item">
                        <label>Отступ (px)</label>
                        <input type="number" value={settings.gap} onChange={(e) => setSettings({...settings, gap: Number(e.target.value)})} min="10" max="50" />
                    </div>
                </div>
            </div>

            <div className="services-list">
                <div className="list-header">
                    <h3>Список курсов (перетаскивайте)</h3>
                    <button onClick={addItem} className="add-service-btn">+ Добавить курс</button>
                </div>

                <div className="services-grid">
                    {items.map((item, idx) => (
                        <div key={item.id} className={`service-item ${!item.visible ? 'hidden' : ''}`} draggable onDragStart={(e) => handleDragStart(e, idx)} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, idx)}>
                            <div className="drag-handle">⋮⋮</div>

                            <div className="service-preview">
                                {item.imageUrl ? <img src={`${item.imageUrl}`} alt="" className="service-image" /> : <div className="service-placeholder">📚</div>}
                            </div>

                            <div className="service-info">
                                <input type="text" value={item.name} onChange={(e) => updateItemField(item.id, 'name', e.target.value)} placeholder="Название" className="service-name-input" />

                                <div className="service-row">
                                    <label className="checkbox-label" style={{ marginRight: 10 }}>
                                        <input type="checkbox" checked={item.isRange} onChange={(e) => updatePriceType(item.id, e.target.checked)} /> Диапазон цен
                                    </label>
                                </div>

                                {item.isRange ? (
                                    <div className="service-row">
                                        <input type="number" value={item.priceMin || ''} onChange={(e) => updateItemField(item.id, 'priceMin', Number(e.target.value))} placeholder="От" className="service-price-input" />
                                        <input type="number" value={item.priceMax || ''} onChange={(e) => updateItemField(item.id, 'priceMax', Number(e.target.value))} placeholder="До" className="service-price-input" />
                                    </div>
                                ) : (
                                    <input type="number" value={item.price || ''} onChange={(e) => updateItemField(item.id, 'price', Number(e.target.value))} placeholder="Цена" className="service-price-input" />
                                )}

                                <input type="text" value={item.duration || ''} onChange={(e) => updateItemField(item.id, 'duration', e.target.value)} placeholder="Длительность" className="service-duration-input" />
                                <textarea value={item.description || ''} onChange={(e) => updateItemField(item.id, 'description', e.target.value)} placeholder="Описание" className="service-desc-input" rows="2" />

                                <div className="service-row">
                                    <label className="upload-image-label">{uploading[item.id] ? '⏳ Загрузка...' : '📸 Загрузить фото'}<input type="file" accept="image/*" onChange={(e) => handleImageUpload(item.id, e.target.files[0])} style={{ display: 'none' }} /></label>
                                    {item.imageUrl && <button onClick={() => updateItemField(item.id, 'imageUrl', null)} className="remove-image-btn">✕ Удалить</button>}
                                </div>
                            </div>

                            <div className="service-actions">
                                <button onClick={() => toggleVisibility(item.id)} className="visibility-btn" title={item.visible ? 'Скрыть' : 'Показать'}>{item.visible ? '👁️' : '👁️‍🗨️'}</button>
                                <button onClick={() => deleteItem(item.id)} className="delete-btn" title="Удалить">🗑️</button>
                            </div>

                            <div className="service-order">{idx + 1}</div>
                        </div>
                    ))}
                </div>
            </div>

            {hasChanges && (
                <div className="save-bottom-banner">
                    <button onClick={handleSaveAll} className="save-all-btn-large">💾 Сохранить все изменения</button>
                </div>
            )}
        </div>
    );
};

export default TrainingManager;